import { VerbRailItem } from '@components/VerbRail/VerbRail';
import {
  READ_ONLY_LIBRARY_REASON,
  verbEmptyLibraryReason,
  verbItem,
  type LibraryVerbId,
} from '@lib/verbs';

interface LibraryRailArgs {
  /** No media yet. Collapses the rail to the one action that can change that. */
  isEmpty?: boolean;
  /** Read-only libraries can't be added to, so Add media is shown dimmed with the reason. */
  isReadOnly?: boolean;
  onWorkWithAgent: () => void;
  onAddMedia?: () => void;
  onFindMoments: () => void;
  /**
   * Keep every slot rather than dropping the ones that don't apply.
   *
   * For rails stacked in a column (the desktop table): a read-only or empty library yields
   * fewer actions, and since the rail is right-aligned, simply omitting them would slide that
   * row's glyphs sideways and break the vertical alignment down the whole table. Cards don't
   * need this — they're not in a column with each other.
   *
   * This flag now only decides what happens to the two VERBS on an empty library — dimmed with
   * a reason in a column, dropped on a card, where collapsing to the accented Add media is the
   * layout's whole point.
   *
   * Add media on a read-only library is dimmed-with-a-reason on BOTH surfaces, regardless of
   * this flag. It used to be an invisible spacer on the grounds that a permanently inert button
   * is noise — that was wrong for the same reason hiding the clip verb on an image was wrong:
   * the user notices the absence, can't tell whether the feature is missing, broken or
   * elsewhere, and is left with nothing to ask. One dimmed button naming the constraint answers
   * it, and every other inert control in these rails now does the same.
   */
  keepLayout?: boolean;
}

/**
 * The library-scoped rail, in one place.
 *
 * Two surfaces render it — the grid card's hover rail and the desktop table's Name
 * cell — and they must agree on the order and the gating. Built here rather than at each
 * call site because the empty and read-only rules below are easy to apply in one place
 * and forget in the other.
 *
 * Labels, glyphs and the default accent come from `@lib/verbs`, so they can't drift from
 * the other verb surfaces either — which is what used to happen (three different names
 * for "Find moments" across the app, and two different glyphs).
 *
 * Mobile has no rail on either view: the grid card reveals one at the centre line,
 * and the list row deliberately has none (no real estate), so mobile reaches these
 * verbs through `MobileLibraryContextDrawer` instead — see its `hasVerbRail` prop.
 *
 * Order is deliberate: Add media sits *between* the two verbs rather than at the
 * end. The verbs are what you do with a library that has media in it; the add sits
 * at the seam so the row reads work → fill → find rather than burying the one
 * action a half-empty library actually needs.
 */
export function buildLibraryRailItems({
  isEmpty,
  isReadOnly,
  onWorkWithAgent,
  onAddMedia,
  onFindMoments,
  keepLayout,
}: LibraryRailArgs): VerbRailItem[] {
  // Two different questions, and conflating them is what used to make Add media vanish without
  // explanation on read-only libraries:
  //
  // - `hasAddMedia` — does this surface offer the action at all? A caller that passes no
  //   handler gets no slot, because there is nothing to explain.
  // - `canAddMedia` — can the user actually act on it here? A read-only library keeps the
  //   button and dims it with a reason, rather than removing it and leaving the user to
  //   wonder whether adding media is missing, broken, or somewhere else.
  const hasAddMedia = !!onAddMedia;
  const canAddMedia = hasAddMedia && !isReadOnly;

  // Both verbs would open a chat about, or a search over, no media at all.
  const showVerbs = !isEmpty;

  // In a column, an empty library shows its verbs dimmed and says what to do about it; on a
  // card it drops them and collapses to the accented Add media, which is that layout's whole
  // point. See `keepLayout`.
  //
  // `canAddMedia` is threaded through because the wording depends on it: with an actionable Add
  // media in the same rail the reason can say "add media first", and on a read-only library —
  // where that button is present but itself dimmed — that would be an instruction the user has
  // no way to follow.
  const emptyVerb = (id: LibraryVerbId) =>
    keepLayout
      ? {
          disabled: true,
          disabledReason: verbEmptyLibraryReason(id, { canAddMedia }),
          // Clears the catalog's default accent, for the same reason Add media below refuses it
          // while disabled: purple means "start here". Without this, a writable empty library in
          // the table renders a dimmed-purple sparkle beside an accented Add media — two accents
          // in one rail, one of them on a control that can't be started. `.disabled` only sets
          // opacity, so the colour survives the dimming.
          accent: false,
        }
      : { hidden: true };

  const items: VerbRailItem[] = [
    {
      ...verbItem('work-with-agent'),
      onClick: onWorkWithAgent,
      ...(showVerbs ? {} : emptyVerb('work-with-agent')),
    },
    {
      ...verbItem('add-media'),
      // Accented only when it's the sole action the user can actually take: on an empty library
      // this IS the "start here", which is what DESIGN.md rations purple for. Beside the two
      // verbs the accent belongs to the agent instead — two purple glyphs in one rail is no
      // accent at all. Overrides the catalog's default (Add media normally isn't accented).
      //
      // Not while it's disabled: purple means "start here", and a control that can't be started
      // shouldn't wear it. A read-only empty library would otherwise show a bright dimmed
      // button, which is two signals contradicting each other.
      accent: isEmpty && canAddMedia,
      disabled: !canAddMedia,
      disabledReason: canAddMedia ? undefined : READ_ONLY_LIBRARY_REASON,
      onClick: onAddMedia ?? (() => undefined),
      hidden: !hasAddMedia,
    },
    {
      ...verbItem('find-moments'),
      onClick: onFindMoments,
      ...(showVerbs ? {} : emptyVerb('find-moments')),
    },
  ];

  return keepLayout ? items : items.filter((item) => !item.hidden);
}
